
import { useForm } from 'react-hook-form';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PEOGuidanceSwitch } from './PEOGuidanceSwitch';
import { PEOSwitches } from './peo/PEOSwitches';
import { PEONeeds } from './peo/PEONeeds';
import { PEOProviderCard } from './PEOProviderCard';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';
import type { Application } from '@/types/database.types';
import type { PEOProvider } from '@/types/peo.types';
import { Input } from '@/components/ui/input';
import { Search } from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';

interface PEOPreferencesProps {
  data: Partial<Application>;
  onSave: (data: Partial<Application>) => void;
}

export const PEOPreferences = ({ data, onSave }: PEOPreferencesProps) => {
  const [isDirectoryOpen, setIsDirectoryOpen] = useState(false);
  const [providers, setProviders] = useState<PEOProvider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<PEOProvider[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { handleSubmit, reset, setValue, watch } = useForm<Partial<Application>>({
    defaultValues: {
      preferred_peos: [],
      excluded_peos: [],
      needs_guidance: false,
      top_3_peo_needs: data.top_3_peo_needs || [],
      required_integrations: data.required_integrations || '',
      open_to_peo_benefits: data.open_to_peo_benefits ?? true,
      open_to_peo_workers_comp: data.open_to_peo_workers_comp ?? true,
      open_to_peo_401k: data.open_to_peo_401k ?? true,
      open_to_peo_epli: data.open_to_peo_epli ?? true,
      open_to_peo_ancillary: data.open_to_peo_ancillary ?? true,
      open_to_peo_eap: data.open_to_peo_eap ?? true,
    },
  });

  useEffect(() => {
    // Mark the section as visited by setting needs_guidance if it hasn't been set yet
    if (data.needs_guidance === null || data.needs_guidance === undefined) {
      onSave({ ...data, needs_guidance: false });
    }
    
    const fetchProviders = async () => {
      const { data } = await supabase
        .from('peo_providers')
        .select('*')
        .order('name');
      
      if (data) {
        // Cast the data to the PEOProvider type
        setProviders(data as unknown as PEOProvider[]);
        setFilteredProviders(data as unknown as PEOProvider[]);
      }
    };

    fetchProviders();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = providers.filter(provider => 
        provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.specialties?.some(specialty => 
          specialty.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredProviders(filtered);
    } else {
      setFilteredProviders(providers);
    }
  }, [searchTerm, providers]);

  const onSubmit = handleSubmit((formData) => {
    onSave(formData);
  });

  return (
    <TooltipProvider>
      <div className="flex flex-col min-h-0 w-full">
        <form onChange={onSubmit} className="flex-1 w-full">
          <div className="space-y-6">
            <Card className="w-full">
              <CardContent className="pt-6 space-y-6">
                <PEOSwitches
                  values={{
                    open_to_peo_benefits: watch('open_to_peo_benefits') ?? true,
                    open_to_peo_workers_comp: watch('open_to_peo_workers_comp') ?? true,
                    open_to_peo_401k: watch('open_to_peo_401k') ?? true,
                    open_to_peo_epli: watch('open_to_peo_epli') ?? true,
                    open_to_peo_ancillary: watch('open_to_peo_ancillary') ?? true,
                    open_to_peo_eap: watch('open_to_peo_eap') ?? true,
                  }}
                  onToggle={(field, value) => {
                    setValue(field, value);
                    onSubmit();
                  }}
                />

                <PEONeeds
                  value={watch('top_3_peo_needs') || []}
                  onChange={(needs) => {
                    setValue('top_3_peo_needs', needs);
                    onSubmit();
                  }}
                />
              </CardContent>
            </Card>

            <PEOGuidanceSwitch
              needsGuidance={watch('needs_guidance')}
              onToggleGuidance={(checked) => {
                setValue('needs_guidance', checked);
                setIsDirectoryOpen(checked);
                if (!checked) {
                  setValue('preferred_peos', []);
                  setValue('excluded_peos', []);
                }
                onSubmit();
              }}
            />

            <Dialog open={isDirectoryOpen} onOpenChange={setIsDirectoryOpen}>
              <DialogContent 
                className="w-[95%] max-w-4xl p-0 border border-primary/20 shadow-lg overflow-hidden"
              >
                <DialogHeader className="p-6 bg-gradient-to-r from-primary/10 to-secondary/20">
                  <DialogTitle className="text-xl font-semibold text-primary text-center">PEO Directory</DialogTitle>
                </DialogHeader>
                
                <div className="p-6 h-[70vh] flex flex-col">
                  <div className="relative mb-6">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or specialty..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-primary/20 focus:border-primary"
                    />
                  </div>
                  
                  <div className="overflow-y-auto flex-1 pr-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6">
                      {filteredProviders.map((provider) => (
                        <PEOProviderCard
                          key={provider.id}
                          provider={provider}
                          isPreferred={data.preferred_peos?.includes(provider.id)}
                          isExcluded={data.excluded_peos?.includes(provider.id)}
                          onTogglePreferred={() => {
                            const preferred = new Set(data.preferred_peos || []);
                            const excluded = new Set(data.excluded_peos || []);
                            
                            if (preferred.has(provider.id)) {
                              preferred.delete(provider.id);
                            } else {
                              preferred.add(provider.id);
                              excluded.delete(provider.id);
                            }
                            
                            setValue('preferred_peos', Array.from(preferred));
                            setValue('excluded_peos', Array.from(excluded));
                            onSubmit();
                          }}
                          onToggleExcluded={() => {
                            const preferred = new Set(data.preferred_peos || []);
                            const excluded = new Set(data.excluded_peos || []);
                            
                            if (excluded.has(provider.id)) {
                              excluded.delete(provider.id);
                            } else {
                              excluded.add(provider.id);
                              preferred.delete(provider.id);
                            }
                            
                            setValue('preferred_peos', Array.from(preferred));
                            setValue('excluded_peos', Array.from(excluded));
                            onSubmit();
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </form>
      </div>
    </TooltipProvider>
  );
};
